import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/types/database";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const isPlaceholderCredentials =
  supabaseUrl === "https://example.supabase.co" ||
  supabaseAnonKey === "public-anon-key-placeholder";

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    "Missing Supabase environment variables. Check .env.example for required variables.",
  );
}

type QueryResult = { data: unknown[] | null; error: null };
type SingleResult = {
  data: null;
  error: { message: string; code: string } | null;
};

function createMockSupabaseClient() {
  const createMockQueryBuilder = () => {
    const state = { isSingle: false, isMaybeSingle: false };

    const resultPromise = new Promise<QueryResult | SingleResult>((resolve) => {
      setTimeout(() => {
        if (state.isSingle) {
          resolve({
            data: null,
            error: { message: "Mock client - no data", code: "PGRST116" },
          });
        } else {
          resolve({ data: [], error: null });
        }
      }, 0);
    });

    const builder = {
      select: () => builder,
      insert: () => builder,
      update: () => builder,
      delete: () => builder,
      upsert: () => builder,
      eq: () => builder,
      neq: () => builder,
      order: () => builder,
      limit: () => builder,
      single: (): Promise<SingleResult> => {
        state.isSingle = true;
        return Promise.resolve({
          data: null,
          error: { message: "Mock client - no data", code: "PGRST116" },
        });
      },
      maybeSingle: (): Promise<{ data: null; error: null }> => {
        state.isMaybeSingle = true;
        return Promise.resolve({ data: null, error: null });
      },
    };

    return new Proxy(builder as object, {
      get: (target, prop) => {
        if (prop === "then" || prop === "catch" || prop === "finally") {
          return (resultPromise as unknown as Record<string, unknown>)[prop];
        }
        return Reflect.get(target, prop);
      },
    });
  };

  return {
    from: createMockQueryBuilder,
    auth: {
      getSession: () =>
        Promise.resolve({ data: { session: null }, error: null }),
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      signInWithPassword: () =>
        Promise.resolve({
          data: { user: null, session: null },
          error: { message: "Mock client" },
        }),
      signOut: () => Promise.resolve({ error: null }),
      onAuthStateChange: () => ({
        data: { subscription: { unsubscribe: () => {} } },
      }),
    },
    storage: {
      from: () => ({
        upload: () =>
          Promise.resolve({ data: null, error: { message: "Mock client" } }),
        download: () =>
          Promise.resolve({ data: null, error: { message: "Mock client" } }),
        remove: () => Promise.resolve({ data: null, error: null }),
        getPublicUrl: () => ({ data: { publicUrl: "" } }),
      }),
    },
    rpc: () => Promise.resolve({ data: null, error: null }),
  } as unknown as ReturnType<typeof createClient<Database>>;
}

export const supabase = isPlaceholderCredentials
  ? createMockSupabaseClient()
  : createClient<Database>(supabaseUrl, supabaseAnonKey);
